import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import axios from 'axios';

import { BFF_BASE_URL } from '../../auth/authApi';
import { PromptVersion } from '../redux/promptsSlice';
import { PromptDiff } from './PromptDiff';
import { GlowAlert } from '../../shared/components/GlowAlert';

interface PromptVersionDialogProps {
    promptKey: string;
    versionId: string | null;
    version: number | null;
    onClose: () => void;
}

export const PromptVersionDialog: React.FC<PromptVersionDialogProps> = ({
    promptKey,
    versionId,
    version,
    onClose,
}) => {
    const [versions, setVersions] = useState<PromptVersion[] | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [mode, setMode] = useState<'prompt' | 'diff'>('prompt');

    useEffect(() => {
        let cancelled = false;
        setVersions(null);
        setError(null);
        setMode('prompt');
        axios
            .get<PromptVersion[]>(
                `${BFF_BASE_URL}/admin/prompts/${encodeURIComponent(promptKey)}/versions`
            )
            .then((response) => {
                if (!cancelled) setVersions(response.data);
            })
            .catch((err) => {
                if (cancelled) return;
                setError(
                    axios.isAxiosError(err)
                        ? err.response?.data?.error || err.message
                        : 'Failed to load prompt version'
                );
            });
        return () => {
            cancelled = true;
        };
    }, [promptKey, versionId]);

    const selected = versions?.find((v) =>
        versionId ? v.id === versionId : v.version === version
    );
    const previous = selected
        ? versions
              ?.filter((v) => v.version < selected.version)
              .sort((x, y) => y.version - x.version)[0]
        : undefined;

    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>
                {promptKey} v{version ?? '?'}{' '}
                {selected?.is_current && (
                    <Chip size="small" color="success" label="current" />
                )}
            </DialogTitle>
            <DialogContent dividers>
                {error && <GlowAlert>{error}</GlowAlert>}
                {!error && !versions && (
                    <GlowAlert severity="info">Loading prompt...</GlowAlert>
                )}
                {versions && !selected && (
                    <Alert severity="warning">
                        This version is no longer available.
                    </Alert>
                )}
                {selected && (
                    <>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                mb: 2,
                                flexWrap: 'wrap',
                            }}
                        >
                            <ToggleButtonGroup
                                size="small"
                                exclusive
                                value={mode}
                                onChange={(_, value) => value && setMode(value)}
                            >
                                <ToggleButton value="prompt">Prompt</ToggleButton>
                                <ToggleButton value="diff">View diff</ToggleButton>
                            </ToggleButtonGroup>
                            <Typography variant="caption" color="text.secondary">
                                Saved {new Date(selected.created_at).toLocaleString()}
                                {selected.created_by_name
                                    ? ` by ${selected.created_by_name}`
                                    : ''}
                            </Typography>
                        </Box>
                        {mode === 'prompt' ? (
                            <Box
                                component="pre"
                                sx={{
                                    m: 0,
                                    fontFamily: 'monospace',
                                    fontSize: 13,
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                }}
                            >
                                {selected.content}
                            </Box>
                        ) : previous ? (
                            <>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: 'block', mb: 1 }}
                                >
                                    Changes from v{previous.version} to v
                                    {selected.version}
                                </Typography>
                                <PromptDiff
                                    before={previous.content}
                                    after={selected.content}
                                    beforeLabel={`v${previous.version}`}
                                    afterLabel={`v${selected.version}`}
                                />
                            </>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                This is the first version, so there is nothing to
                                compare it to.
                            </Typography>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};
